import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAuthService } from '@/domains/admin/auth/services/adminAuthService';
import { Button } from '@/shared/components/ui';
import logoVector1 from '@/assets/logo-vector.svg';
import logoVector2 from '@/assets/logo-vector-2.svg';
import logoVector3 from '@/assets/logo-vector-3.svg';

const AdminLogin: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (adminAuthService.isAuthenticated()) {
      navigate('/admin/tenants', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await adminAuthService.login({ username, password });
      // Use window.location for hard redirect to avoid React rendering issues
      window.location.href = '/admin/tenants';
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-lg px-8 py-6">
          {/* Logo and Header */}
          <div className="flex flex-col gap-5 mb-8">
            <div className="flex justify-center">
              <div className="text-center">
                <div className="relative w-[138px] h-[94px] mx-auto">
                  <img
                    src={logoVector1}
                    alt="Logo part 1"
                    className="absolute top-0 left-[34.984px] w-[68.735px] h-[67.681px]"
                    style={{ fill: 'var(--color-primary)' }}
                  />
                  <img
                    src={logoVector2}
                    alt="Logo part 2"
                    className="absolute top-[3.462px] left-[23.639px] w-[84.059px] h-[59.822px]"
                    style={{ fill: 'var(--color-primary)' }}
                  />
                  <img
                    src={logoVector3}
                    alt="Logo part 3"
                    className="absolute top-[56.581px] left-[22.772px] w-[21.409px] h-[11.076px]"
                    style={{ fill: 'var(--color-primary)' }}
                  />
                  <div className="absolute top-[75.822px] left-0 w-full text-[var(--color-primary)] text-[7.736px] tracking-wide">
                    ANAND IMAGING & DIAGNOSTICS
                  </div>
                </div>
              </div>
            </div>
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-[var(--color-text-primary)] mb-1">Admin Portal</h2>
              <p className="text-sm text-[var(--color-text-muted)]">Sign in to manage tenants</p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-6">
              {/* Username Field */}
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="username"
                  className="text-sm font-medium text-[var(--color-text-primary)] tracking-wide"
                >
                  Username
                </label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="7" r="3.5" stroke="#ADAAAA" strokeWidth="1.5"/>
                      <path d="M4 17c0-2.5 2.5-5 6-5s6 2.5 6 5" stroke="#ADAAAA" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    autoComplete="username"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    className="w-full pl-11 pr-3.5 py-3 border border-[#e5e2e1] rounded-lg text-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="flex flex-col gap-1">
                <label
                  htmlFor="password"
                  className="text-sm font-medium text-[var(--color-text-primary)] tracking-wide"
                >
                  Password
                </label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M5 10h10M10 5v10" stroke="#ADAAAA" strokeWidth="1.5" strokeLinecap="round"/>
                      <rect x="4" y="8" width="12" height="7" rx="1" stroke="#ADAAAA" strokeWidth="1.5"/>
                      <path d="M7 8V6a3 3 0 0 1 6 0v2" stroke="#ADAAAA" strokeWidth="1.5"/>
                    </svg>
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full pl-11 pr-11 py-3 border border-[#e5e2e1] rounded-lg text-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent"
                  />
                  <Button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    variant="neutral"
                    className="!absolute !right-3.5 !top-1/2 !-translate-y-1/2 !text-[var(--color-text-muted)] hover:!text-[var(--color-text-primary)] !bg-transparent !border-none !p-0 !h-auto"
                    padding="p-0"
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      {showPassword ? (
                        <>
                          <path d="M2 2L18 18M9.5 9.5C9 10 9 11 9.5 11.5C10 12 11 12 11.5 11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <path d="M6.5 6.5C4.5 7.5 3 9.5 3 10C3 10.5 6 15 10 15C11 15 12 14.8 13 14.3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <path d="M17 10C17 9.5 14 5 10 5C9.5 5 9 5.1 8.5 5.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </>
                      ) : (
                        <>
                          <path d="M10 13C11.6569 13 13 11.6569 13 10C13 8.34315 11.6569 7 10 7C8.34315 7 7 8.34315 7 10C7 11.6569 8.34315 13 10 13Z" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M3 10C3 10 6 5 10 5C14 5 17 10 17 10C17 10 14 15 10 15C6 15 3 10 3 10Z" stroke="currentColor" strokeWidth="1.5"/>
                        </>
                      )}
                    </svg>
                  </Button>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isLoading}
              variant="primary"
              fullWidth
              padding="py-3 px-6"
              fontSize="text-base"
              className="tracking-wide focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-2"
            >
              {isLoading ? 'Signing in...' : 'Sign in as Admin'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
